/**
 * The AI notice at contribution (TMMAPP-0142 U3 item 19; TMMAPI-0181 U6, MM-D-146). When an
 * allocation into a virtual account the person doesn't own answers `aiNoticeRequired`, the one-line
 * notice shows once, after the allocation succeeded: OK records `POST /v1/me/consents {kind:
 * AI_CONTRIBUTIONS, version: aiNoticeVersion}`, and a link opens the AI setting ("Keep my
 * contributions out of AI", Settings → Subscription). It never blocks the allocation, and it is
 * neither a dialog nor a toast (the app's one snackbar is `NotifyProvider`'s, and a toast fades): a
 * polite banner at the top of the page that waits until answered or closed. Mounted once, in the
 * shell, above the page.
 */
import { useState, useSyncExternalStore } from 'react';
import { Alert, Button, IconButton, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiContributionNotice } from '@/api/aiContributionNotice';
import { consentsApi } from '@/api/consents';
import { errorCopy } from '@/api/errorCopy';
import { CloseIcon } from '@/components/icons';
import { settingsHref } from '@/features/settings/settingsHref';

export function AiContributionNotice() {
  const { t } = useTranslation();
  const version = useSyncExternalStore(
    aiContributionNotice.subscribe,
    aiContributionNotice.snapshot,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (version === null) return null;

  async function acknowledge(current: string) {
    setBusy(true);
    setError(null);
    try {
      await consentsApi.record('AI_CONTRIBUTIONS', current);
      aiContributionNotice.clear();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Alert
      severity="info"
      role="status"
      data-ai-contribution-notice=""
      action={
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
          <Button
            color="inherit"
            size="small"
            disabled={busy}
            onClick={() => void acknowledge(version)}
          >
            {t('assistant.aiNotice.ok')}
          </Button>
          <IconButton
            color="inherit"
            size="small"
            aria-label={t('common.close')}
            onClick={() => aiContributionNotice.clear()}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      }
    >
      <Stack sx={{ gap: 0.5 }}>
        <span>{t('assistant.aiNotice.text')}</span>
        <Link
          component={RouterLink}
          to={settingsHref('ai-automation')}
          onClick={() => aiContributionNotice.clear()}
        >
          {t('assistant.aiNotice.setting')}
        </Link>
        {error !== null && <span>{errorCopy(t, error, 'assistant.error.generic')}</span>}
      </Stack>
    </Alert>
  );
}
